import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, retry, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionStorageService } from './storage.service';
import { UserService } from './user.service';

/** The account fields the IAM account PUT accepts. The endpoint replaces the whole record, so every field has to be echoed back on an update - anything left out is cleared. */
export interface IUpdateAccountRequest {
	name: string;
	allowedIPs: string[];
	defaultEngine: string | null;
}

export interface IAccountDetailsResponse {
	result: {
		name: string;
		allowedIPs: string[];
		defaultEngine?: string | null;
	};
}

@Injectable({
	providedIn: 'root',
})
export class AccountService {
	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {}

	private getHeaders() {
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
		});
	}

	public getAccount(): Observable<IAccountDetailsResponse> {
		const account = this.userService.account;
		return this.http.get<IAccountDetailsResponse>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}`, { headers: this.getHeaders() }).pipe(retry(1), catchError(this.handleError));
	}

	/** Updates the account. `defaultEngine` is a required argument on purpose: the PUT replaces the whole account, so leaving it out of the body wipes the account's default engine. Pass the value read back from `getAccount()` so an update never changes it implicitly. */
	public updateAccount(name: string, allowedIPs: string[], defaultEngine: string | null): Observable<IUpdateAccountRequest> {
		const account = this.userService.account;
		const body: IUpdateAccountRequest = { name, allowedIPs, defaultEngine };
		return this.http
			.put<IUpdateAccountRequest>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}`, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}
